import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../lib/apiClient';

/**
 * Fetches dashboard data for kacheln from backend APIs.
 * Returns weather, subsidies, and mock data for other kacheln.
 */
export function useDashboard(token) {
  const [weather, setWeather] = useState(null);
  const [subsidies, setSubsidies] = useState(null);
  const [sprayWindows, setSprayWindows] = useState(null);
  const [fieldwork, setFieldwork] = useState(null);
  const [market, setMarket] = useState(null);
  const [finance, setFinance] = useState(null);
  const [cashflow, setCashflow] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);

    const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

    const [weatherRes, subsidyRes, sprayRes, fieldworkRes, marketRes, financeRes, cashflowRes] = await Promise.allSettled([
      fetch('/api/v1/aif/weather/forecast?days=7', { headers }),
      apiClient.post('/api/v1/aif/subsidies/match', {}, { queueOnOffline: false }),
      fetch('/api/v1/aif/weather/spray-window?hours_ahead=168', { headers }),
      fetch('/api/v1/aif/weather/fieldwork?days=7', { headers }),
      fetch('/api/v1/aif/market/prices', { headers }),
      fetch('/api/v1/aif/finance/contribution-margin', { headers }),
      fetch('/api/v1/aif/finance/cashflow', { headers }),
    ]);

    if (weatherRes.status === 'fulfilled' && weatherRes.value.ok) {
      try { setWeather(await weatherRes.value.json()); } catch { /* ignore */ }
    }
    if (subsidyRes.status === 'fulfilled' && subsidyRes.value.ok) {
      try {
        const raw = subsidyRes.value.data; // apiClient already parsed JSON
        // Map API response (matches[].program + estimated_amount) to frontend format (matched_programs[])
        if (raw.matches && !raw.matched_programs) {
          raw.matched_programs = raw.matches
            .filter(m => m.eligible)
            .map(m => ({
              name: m.program?.name_de || m.program?.name || '',
              description: m.program?.description_de || '',
              estimated_amount_eur: m.estimated_amount || 0,
              eligibility: m.eligible ? 'eligible' : 'ineligible',
              deadline: m.program?.deadline || null,
              country_code: m.program?.country_code || '',
              category: m.program?.category || '',
              framework: m.program?.framework || '',
              application_portal: m.program?.application_portal || '',
              link: m.program?.link || '',
              source_authority: m.source_authority || '',
              eligibility_reason: m.eligibility_reason || '',
            }));
        }
        setSubsidies(raw);
      } catch { /* ignore */ }
    }
    if (sprayRes.status === 'fulfilled' && sprayRes.value.ok) {
      try { setSprayWindows(await sprayRes.value.json()); } catch { /* ignore */ }
    }
    if (fieldworkRes.status === 'fulfilled' && fieldworkRes.value.ok) {
      try { setFieldwork(await fieldworkRes.value.json()); } catch { /* ignore */ }
    }
    if (marketRes.status === 'fulfilled' && marketRes.value.ok) {
      try { setMarket(await marketRes.value.json()); } catch { /* ignore */ }
    }

    if (financeRes.status === 'fulfilled' && financeRes.value.ok) {
      try { setFinance(await financeRes.value.json()); } catch { /* ignore */ }
    }

    if (cashflowRes.status === 'fulfilled' && cashflowRes.value.ok) {
      try { setCashflow(await cashflowRes.value.json()); } catch { /* ignore */ }
    }

    setLoading(false);
  }, [token]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 15 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchData]);

  return { weather, subsidies, sprayWindows, fieldwork, market, finance, cashflow, loading, refresh: fetchData };
}

// Mock data for kacheln without API endpoints yet
export const MOCK_FIELDS = {
  totalHa: 185,
  cropCount: 6,
  fields: [
    { name: 'Feld Nord', ha: 45, crop: 'Winterweizen', soil: 'Parabraunerde' },
    { name: 'Feld Süd', ha: 38, crop: 'Wintergerste', soil: 'Braunerde' },
    { name: 'Schlag Ost', ha: 52, crop: 'Raps', soil: 'Schwarzerde' },
    { name: 'Grünland West', ha: 30, crop: 'Grünland', soil: 'Gley' },
    { name: 'Maisfeld', ha: 20, crop: 'Silomais', soil: 'Braunerde' },
  ],
};

// MOCK_MARKET removed in Sprint 13 — real data from API.
// Kept as empty stub for legacy panels (LeftPanel, RightPanel).
export const MOCK_MARKET = { prices: [], history: [] };

export const MOCK_HERD = {
  headCount: 120,
  avgMilkYield: 32.5,
  unit: 'kg/Tag',
};

export const MOCK_ENERGY = {
  pvTodayKwh: 142.7,
  pvCapacityKwp: 49.9,
};

export const MOCK_COMPLIANCE = {
  status: 'yellow', // 'green' | 'yellow' | 'red'
  issues: 2,
  total: 12,
};
